import defaults from 'lodash/defaults';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _options, _attempts, _currentOperation, _nextDelay, _resetTimeout, _wasExecuted, _execute;
const _Backoff = class _Backoff {
  /**
   * Constructor for Backoff class.
   *
   * @param {Object} options - Options for the backoff.
   * @param {number} options.maxAttempts - Maximum number of attempts.
   * @param {number} options.baseDelay - Delay for the first attempt.
   * @param {number} options.backoffFactor - Factor by which the delay increases.
   * @param {number} options.fuzzFactor - Factor by which to randomize the delay.
   * @param {number} options.resetTime - Time to elapse before resetting the backoff.
   */
  constructor(options) {
    __privateAdd(this, _options, void 0);
    __privateAdd(this, _attempts, void 0);
    __privateAdd(this, _currentOperation, void 0);
    __privateAdd(this, _nextDelay, void 0);
    __privateAdd(this, _resetTimeout, void 0);
    __privateAdd(this, _wasExecuted, false);
    /**
     * Private method to execute the operation.
     */
    __privateAdd(this, _execute, () => {
      __privateGet(this, _options).operation();
      __privateSet(this, _currentOperation, void 0);
      __privateSet(this, _wasExecuted, true);
    });
    __privateSet(this, _options, defaults({}, options, _Backoff.defaultOptions));
    __privateSet(this, _attempts, 0);
    __privateSet(this, _nextDelay, __privateGet(this, _options).baseDelay);
    __privateSet(this, _currentOperation, void 0);
    __privateSet(this, _resetTimeout, void 0);
    this.reset = this.reset.bind(this);
  }
  /**
   * Set options for the backoff.
   *
   * @param {Object} options - Options for the backoff.
   * @param {number} options.maxAttempts - Maximum number of attempts.
   * @param {number} options.baseDelay - Delay for the first attempt.
   * @param {number} options.backoffFactor - Factor by which the delay increases.
   * @param {number} options.fuzzFactor - Factor by which to randomize the delay.
   * @param {number} options.resetTime - Time to elapse before resetting the backoff.
   */
  setOptions(options) {
    __privateSet(this, _options, defaults({}, options, __privateGet(this, _options), _Backoff.defaultOptions));
    if (__privateGet(this, _attempts) === 0)
      __privateSet(this, _nextDelay, __privateGet(this, _options).baseDelay);
  }
  /**
   * Attempt to execute the operation.
   *
   * @returns {boolean} - Whether or not the operation was attempted.
   */
  attempt() {
    this.cancelReset();
    if (__privateGet(this, _currentOperation))
      return true;
    if (__privateGet(this, _attempts) >= __privateGet(this, _options).maxAttempts) {
      return false;
    }
    const delay = _Backoff.fuzz(__privateGet(this, _nextDelay), __privateGet(this, _options).fuzzFactor);
    __privateSet(this, _attempts, __privateGet(this, _attempts) + 1);
    __privateSet(this, _currentOperation, setTimeout(__privateGet(this, _execute), delay));
    __privateSet(this, _nextDelay, __privateGet(this, _nextDelay) * __privateGet(this, _options).backoffFactor);
    return true;
  }
  /**
   * Resets the backoff to its initial state.
   */
  reset() {
    if (__privateGet(this, _resetTimeout)) {
      clearTimeout(__privateGet(this, _resetTimeout));
      __privateSet(this, _resetTimeout, void 0);
    }
    if (__privateGet(this, _currentOperation)) {
      clearTimeout(__privateGet(this, _currentOperation));
      __privateSet(this, _currentOperation, void 0);
    }
    __privateSet(this, _attempts, 0);
    __privateSet(this, _nextDelay, __privateGet(this, _options).baseDelay);
  }
  /**
   * Schedule a reset if the backoff  has been executed before.
   * If the reset time is set to 0, do not schedule a reset.
   */
  scheduleReset() {
    if (!__privateGet(this, _wasExecuted))
      return;
    __privateSet(this, _wasExecuted, false);
    this.cancelReset();
    if (__privateGet(this, _options).resetTime !== 0) {
      __privateSet(this, _resetTimeout, setTimeout(this.reset, __privateGet(this, _options).resetTime));
    }
  }
  /**
   * Cancels the reset timeout if it exists.
   */
  cancelReset() {
    if (__privateGet(this, _resetTimeout)) {
      clearTimeout(__privateGet(this, _resetTimeout));
    }
  }
  /**
   * Destroy the object by resetting it
   */
  destroy() {
    this.reset();
  }
  /**
   * Fuzzes a numeric value by adding a random factor between -fuzzFactor and +fuzzFactor.
   * @param {number} value - The original numeric value to be fuzzed.
   * @param {number} fuzzFactor - The maximum absolute value of the random factor to be added.
   * @returns {number} The original value, fuzzed by +/- fuzzFactor.
   */
  static fuzz(value, fuzzFactor) {
    const negToPosOne = Math.random() * 2 - 1;
    const negToPosFuzzFactor = negToPosOne * fuzzFactor;
    return value * (1 + negToPosFuzzFactor);
  }
};
_options = new WeakMap();
_attempts = new WeakMap();
_currentOperation = new WeakMap();
_nextDelay = new WeakMap();
_resetTimeout = new WeakMap();
_wasExecuted = new WeakMap();
_execute = new WeakMap();
/**
 * Default options for Backoff class
 * resetTime is the number of milliseconds to wait before auto resetting the backoff
 * The rest has the same semantics as Shaka retryParameters
 * https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.RetryParameters
 */
__publicField(_Backoff, "defaultOptions", {
  maxAttempts: 2,
  baseDelay: 1e3,
  backoffFactor: 2,
  fuzzFactor: 0.5,
  resetTime: 2e4
});
let Backoff = _Backoff;

export { Backoff as default };
//# sourceMappingURL=Backoff.js.map
